<template>
  <li
    class="create-option-row"
    :class="{ 'is-highlighted': highlighted }"
    data-test="combobox-create-option"
    role="option">
    <div class="create-option-icon"><IconPlus size="18" /></div>
    <span class="create-option-name">{{ label }}</span>
    <span class="create-option-badge">{{ badgeLabel }}</span>
  </li>
</template>

<script setup lang="ts">
import { IconPlus } from '@tabler/icons-vue';
import translator from '@/locale/translator';

// The row a free-text combobox shows for the value the user typed when no
// existing option matches it. Rendered by both LeadCombobox and
// LeadTreeCombobox so a new entry looks the same wherever it is offered.
//
// Deliberately does not look like a normal option: the plus tile and badge are
// what tell the user this will create something rather than select something
// that already exists. Click and hover handlers fall through from the parent
// onto the root element.
defineProps<{
  label: string;
  highlighted?: boolean;
}>();

const badgeLabel = translator.get('common.new');
</script>

<style scoped>
/* Mirrors the row metrics of the option rows in both host comboboxes (42px
   tall, 4px leading padding, 34px leading tile) so it reads as one list. */
.create-option-row {
  list-style: none;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 4px;
  height: 42px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.create-option-row.is-highlighted {
  background-color: #f3f4f6;
}

.create-option-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eff6ff;
  color: var(--tblr-primary);
}

.create-option-name {
  flex-grow: 1;
  font-size: 16px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.create-option-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: #eff6ff;
  color: var(--tblr-primary);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
</style>
